/**
 * Visual range bar showing parameter value position within acceptable range.
 * Uses Mantine Progress component with custom styling.
 * Supports compact rendering at 100px width for mobile layouts.
 */
import { useMemo } from 'react';
import { Box, Progress, Text } from '@mantine/core';
import { parseRange, parseDesiredLevel } from '@/utils/rangeParser';

interface RangeBarProps {
  value: number;
  rangeStr?: string;
  desiredStr?: string;
  inRange: boolean;
  /** Hide min/max labels for compact display (default: false) */
  compact?: boolean;
}

export function RangeBar({ value, rangeStr, desiredStr, inRange, compact = false }: RangeBarProps) {
  // Memoize parsed range values to avoid recalculating on every render
  const parsedRange = useMemo(() => {
    if (!rangeStr) return null;
    return parseRange(rangeStr);
  }, [rangeStr]);

  const parsedDesired = useMemo(() => {
    if (!desiredStr) return null;
    return parseDesiredLevel(desiredStr);
  }, [desiredStr]);

  if (!rangeStr) {
    return <Text size="xs" c="dimmed">No range</Text>;
  }

  if (!parsedRange) {
    return <Text size="xs" c="dimmed">Invalid range</Text>;
  }

  const { min, max } = parsedRange;
  const span = max - min;

  // Calculate percentage position (0-100)
  const percentage = ((value - min) / span) * 100;

  // Clamp to 0-100 for display
  const displayPercentage = Math.max(0, Math.min(100, percentage));

  // Calculate desired percentage from memoized value
  const desiredPercentage = parsedDesired !== null
    ? ((parsedDesired - min) / span) * 100
    : null;

  // Use smaller minWidth in compact mode for mobile layouts
  const minWidth = compact ? 80 : 120;

  return (
    <Box style={{ position: 'relative', width: '100%', minWidth }}>
      {/* Range bar background */}
      <Progress
        value={100}
        size={compact ? 'md' : 'lg'}
        color={inRange ? 'gray.3' : 'red.2'}
        style={{ marginBottom: compact ? 0 : 4 }}
      />

      {/* Current value marker */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: `${displayPercentage}%`,
          transform: 'translateX(-50%)',
          width: compact ? 2 : 3,
          height: compact ? 20 : 24,
          backgroundColor: inRange ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)',
          borderRadius: 2,
        }}
      />

      {/* Desired level marker (if provided) */}
      {desiredPercentage !== null && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: `${Math.max(0, Math.min(100, desiredPercentage))}%`,
            transform: 'translateX(-50%)',
            width: 2,
            height: compact ? 20 : 24,
            backgroundColor: 'var(--mantine-color-blue-5)',
            opacity: 0.5,
            borderRadius: 1,
          }}
        />
      )}

      {/* Range labels - hidden in compact mode */}
      {!compact && (
        <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <Text size="xs" c="dimmed">{min}</Text>
          <Text size="xs" c="dimmed">{max}</Text>
        </Box>
      )}
    </Box>
  );
}
